/** Successful reads are shared across pages; failures remain retryable. */
export function createReadCache<T>(ttlMs: number) {
  type Entry = { value?: T; expiresAt: number; pending?: Promise<T> };
  const entries = new Map<string, Entry>();
  return {
    clear: () => entries.clear(),
    read(key: string, load: () => Promise<T>, options: { force?: boolean; accept?: (value: T) => boolean } = {}): Promise<T> {
      const existing = entries.get(key);
      if (!options.force && existing) {
        if (existing.pending) return existing.pending;
        if (existing.expiresAt > Date.now() && existing.value !== undefined && (!options.accept || options.accept(existing.value))) {
          return Promise.resolve(existing.value);
        }
      }
      for (const [id, entry] of entries) if (!entry.pending && entry.expiresAt <= Date.now()) entries.delete(id);
      const entry: Entry = { expiresAt: 0 };
      entries.set(key, entry);
      const pending = Promise.resolve().then(load).then(value => {
        if (entries.get(key) === entry) { entry.value = value; entry.expiresAt = Date.now() + ttlMs; entry.pending = undefined; }
        return value;
      }, error => { if (entries.get(key) === entry) entries.delete(key); throw error; });
      entry.pending = pending;
      return pending;
    },
  };
}

// A component unmount must not cancel a read shared by another screen/header.
export function withReadAbort<T>(request: Promise<T>, signal?: AbortSignal): Promise<T> {
  if (!signal) return request;
  return new Promise((resolve, reject) => {
    const abort = () => reject(new DOMException('The request was aborted.', 'AbortError'));
    if (signal.aborted) abort();
    else signal.addEventListener('abort', abort, { once: true });
    request.then(value => { signal.removeEventListener('abort', abort); resolve(value); },
      error => { signal.removeEventListener('abort', abort); reject(error); });
  });
}
